/**
 * legacyNaturePropTextures — SVG generators for environmental prop bodies
 * (Tiers 1, 1.5, 2, 3 and Lanternwood landmark). Extracted from propTextures.ts
 * to respect the 500-LOC decomposition invariant.
 */

/** Convert a 0xRRGGBB number to an SVG `#rrggbb` hex string. */
export function hex(color: number): string {
  return '#' + color.toString(16).padStart(6, '0');
}

/** Per-channel darken (factor 0..1) — unlike a whole-int multiply, this never
 *  bleeds across channels. */
export function darken(color: number, factor: number): number {
  const r = Math.round(((color >> 16) & 0xff) * factor);
  const g = Math.round(((color >> 8) & 0xff) * factor);
  const b = Math.round((color & 0xff) * factor);
  return (r << 16) | (g << 8) | b;
}

// ─── Tier-1 generators ──────────────────────────────────────────────────────

/** Palm: bent wind-swept trunk (wheat brown → darker at base) + 6 frond arcs
 *  + 3 coconuts. */
export function palmBody(frond: number): string {
  const f = hex(frond);
  const fDark = hex(darken(frond, 0.7));
  return `
    <defs>
      <linearGradient id="trunk" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="#8b7355" />
        <stop offset="1" stop-color="#6b5a3a" />
      </linearGradient>
    </defs>
    <path d="M30 120 Q34 78 48 34 L60 36 Q44 82 44 120 Z" fill="url(#trunk)" />
    <path d="M35 100 q6 2 10 1 M40 76 q6 2 9 1 M46 52 q5 2 8 1"
      stroke="#6b5a3a" stroke-width="1.5" fill="none" opacity="0.6" />
    <path d="M55 32 Q28 20 8 34 Q30 30 55 38 Z" fill="${f}" />
    <path d="M55 32 Q34 8 14 10 Q38 18 55 36 Z" fill="${fDark}" />
    <path d="M55 32 Q50 4 30 2 Q52 12 57 34 Z" fill="${f}" />
    <path d="M55 32 Q64 2 84 6 Q62 14 57 34 Z" fill="${fDark}" />
    <path d="M55 32 Q78 12 88 28 Q72 24 55 38 Z" fill="${f}" />
    <path d="M55 32 Q80 34 88 48 Q70 40 54 40 Z" fill="${fDark}" />
    <circle cx="48" cy="38" r="4" fill="#4a3020" />
    <circle cx="58" cy="40" r="4" fill="#4a3020" />
    <circle cx="53" cy="44" r="3.5" fill="#4a3020" />`;
}

/** Beach rock: a low sun-bleached rounded 2-stone cluster, sand clinging at base. */
export function beachRockBody(stone: number, sand: number): string {
  const s = hex(stone);
  const mid = hex(darken(stone, 0.9));
  return `
    <ellipse cx="32" cy="37" rx="28" ry="4" fill="${hex(sand)}" opacity="0.55" />
    <ellipse cx="26" cy="25" rx="22" ry="15" fill="${s}" />
    <ellipse cx="48" cy="30" rx="14" ry="10" fill="${mid}" />
    <path d="M6 28 Q26 42 47 31 Q48 38 40 39 Q16 40 6 28 Z" fill="#808080" opacity="0.8" />
    <ellipse cx="20" cy="18" rx="9" ry="4" fill="#ffffff" opacity="0.18" />`;
}

/** Tide pool: faceted stone/sand basin with translucent layered water depth and crisp surface ripples. */
export function tidePoolBody(
  water: number,
  sand: number,
  style: 'beach' | 'flooded' = 'beach',
): string {
  const star = '#d4a060';
  const starPath = 'M0 -4 L1.2 -1.2 L4 -1 L1.8 0.8 L2.4 3.6 L0 1.8 L-2.4 3.6 L-1.8 0.8 L-4 -1 L-1.2 -1.2 Z';
  const sandHex = hex(sand);
  const sandDarkHex = hex(darken(sand, 0.82));
  const waterHex = hex(water);

  const details = style === 'flooded'
    ? `
    <path d="M12 22 q3 -10 2 -15 M18 23 q-2 -8 2 -14 M80 22 q-3 -9 -1 -14"
      stroke="#263b3e" stroke-width="1.8" fill="none" opacity="0.9" />
    <circle cx="34" cy="18" r="1.8" fill="none" stroke="#6f93a3" stroke-width="0.9" opacity="0.75" />
    <circle cx="62" cy="13" r="1.3" fill="none" stroke="#6f93a3" stroke-width="0.8" opacity="0.7" />
    <path d="M22 12 Q42 8 62 12 Q74 15 64 17 Q40 13 22 12 Z" fill="#6f93a3" opacity="0.32" />
    <path d="M30 19 Q48 16 66 19" stroke="#6f93a3" stroke-width="0.8" fill="none" opacity="0.5" />`
    : `
    <g transform="translate(32, 19)"><path d="${starPath}" fill="${star}" /></g>
    <g transform="translate(63, 17) scale(0.8)"><path d="${starPath}" fill="${star}" /></g>
    <path d="M22 12 Q42 7 64 11 Q76 14 62 16 Q38 13 22 12 Z" fill="#a0d8f0" opacity="0.45" />
    <path d="M28 17 Q48 14 68 18" stroke="#a0d8f0" stroke-width="0.9" fill="none" opacity="0.6" />`;

  return `
    <!-- Outer faceted basin bank -->
    <path d="M5 16 L12 8 L28 4 L68 3 L85 7 L93 15 L88 24 L72 29 L26 30 L9 25 Z" fill="${sandHex}" />
    <!-- Inner faceted shadow rim -->
    <path d="M9 16 L16 9 L30 6 L66 5 L82 9 L89 16 L84 23 L69 27 L28 28 L13 23 Z" fill="${sandDarkHex}" />
    <!-- Deep water basin with faceted contour -->
    <path d="M12 16 L18 10 L32 7 L64 6 L80 10 L86 16 L82 22 L67 25 L30 26 L15 22 Z" fill="${waterHex}" opacity="0.75" />
    <!-- Secondary translucent water shelf -->
    <path d="M16 16 L22 11 L35 9 L61 8 L76 11 L82 16 L78 21 L64 24 L32 25 L19 21 Z" fill="${waterHex}" opacity="0.55" />
    ${details}`;
}

/** Which trinket variant a tint maps to. */
export function trinketVariant(tint: number): 'shell' | 'sea_glass' | 'rope_coil' {
  const r = (tint >> 16) & 0xff;
  const g = (tint >> 8) & 0xff;
  const b = tint & 0xff;
  if (b > r) return 'sea_glass';
  if (r > 0xc0 && g >= r * 0.8) return 'shell';
  return 'rope_coil';
}

/** Beachcomber trinkets: tiny shell / sea-glass / rope-coil quest props. */
export function trinketBody(tint: number): string {
  const c = hex(tint);
  switch (trinketVariant(tint)) {
  case 'sea_glass':
    return `
        <rect x="7" y="5" width="10" height="14" rx="4.5" fill="${c}" opacity="0.85" />
        <rect x="9" y="7" width="3" height="8" rx="1.5" fill="#ffffff" opacity="0.4" />
        <rect x="7" y="5" width="10" height="14" rx="4.5" fill="none"
          stroke="${hex(darken(tint, 0.75))}" stroke-width="1" opacity="0.6" />`;
  case 'rope_coil':
    return `
        <circle cx="12" cy="13" r="8" fill="none" stroke="${c}" stroke-width="2.4" />
        <circle cx="12" cy="13" r="4.5" fill="none" stroke="${hex(darken(tint, 0.78))}" stroke-width="2.2" />
        <path d="M19 8 Q22 5 21 2" fill="none" stroke="${c}" stroke-width="2.2" stroke-linecap="round" />`;
  case 'shell':
  default:
    return `
        <circle cx="12" cy="12" r="8.5" fill="${c}" />
        <path d="M12 12 q3.5 -1 3.5 2.5 q0 4 -5 4 q-6 0 -6 -6.5 q0 -7.5 8 -7.5"
          fill="none" stroke="#d4a060" stroke-width="1.4" stroke-linecap="round" />`;
  }
}

/** Pig pen: low weathered 3-post fence with two rail slats. */
export function pigPenBody(rail: number): string {
  const r = hex(rail);
  const post = '#80654a';
  return `
    <rect x="10" y="8" width="7" height="38" rx="2" fill="${post}" />
    <rect x="52" y="6" width="7" height="40" rx="2" fill="${post}" />
    <rect x="94" y="9" width="7" height="37" rx="2" fill="${post}" />
    <rect x="3" y="15" width="105" height="6" rx="2.5" fill="${r}" />
    <rect x="5" y="30" width="102" height="6" rx="2.5" fill="${r}" />
    <path d="M3 20.5 h105 M5 35.5 h102" stroke="${hex(darken(rail, 0.8))}" stroke-width="1.2" opacity="0.7" />
    <path d="M24 17 h12 M70 32 h14" stroke="${hex(darken(rail, 0.85))}" stroke-width="1" opacity="0.5" />`;
}

// ─── Tier-1.5 generators ────────────────────────────────────────────────────

/** Village well: circular stone base + wooden A-frame roof. */
export function villageWellBody(stone: number): string {
  const s = hex(stone);
  const sDark = hex(darken(stone, 0.76));
  return `
    <rect x="9" y="22" width="5" height="38" fill="#6a4a32" />
    <rect x="50" y="22" width="5" height="38" fill="#6a4a32" />
    <path d="M2 26 L32 6 L62 26 L56 30 L32 14 L8 30 Z" fill="#6a4a32" />
    <path d="M2 26 L32 6 L62 26 L58 28 L32 11 L6 28 Z" fill="#4a3020" opacity="0.55" />
    <rect x="12" y="30" width="40" height="3" rx="1.5" fill="#4a3020" />
    <path d="M32 33 v14" stroke="#4a3020" stroke-width="1.4" />
    <rect x="28" y="47" width="8" height="7" rx="1.5" fill="#6a4a32" />
    <ellipse cx="32" cy="60" rx="23" ry="6" fill="${s}" />
    <ellipse cx="32" cy="59" rx="17" ry="4" fill="#4a3a5a" />
    <path d="M9 60 v14 q0 4 5 4 h36 q5 0 5 -4 v-14" fill="${s}" />
    <path d="M9 66 h46 M17 60.5 v17 M32 62 v16 M47 60.5 v17"
      stroke="${sDark}" stroke-width="1.5" opacity="0.7" />
    <path d="M9 73 q23 6 46 0 v1 q0 4 -5 4 h-36 q-5 0 -5 -4 Z" fill="${sDark}" opacity="0.8" />`;
}

/** Autumn leaf: single drifting leaf. */
export function autumnLeafBody(leaf: number): string {
  const c = hex(leaf);
  return `
    <path d="M10 3 Q17 5 16.5 11.5 Q15 17.5 8.5 17 Q3 15 3.5 8.5 Q5.5 4 10 3 Z" fill="${c}" />
    <path d="M10 3 Q17 5 16.5 11.5 Q13 13 10 10 Q8 6 10 3 Z" fill="#8b4513" opacity="0.45" />
    <path d="M10 4 Q11 10 9 16 M10 8 q3 0 4.5 -1.5 M9.5 12 q-3 0 -4 -2"
      fill="none" stroke="#8b4513" stroke-width="0.9" opacity="0.8" />
    <path d="M9 16 Q7.5 18 6 19.5" fill="none" stroke="#4a2a0a" stroke-width="1.3" stroke-linecap="round" />`;
}

// ─── Tier-2 generators ──────────────────────────────────────────────────────

/** Dead bracken: twisted, leafless bush with dark angular stems. */
export function deadBrackenBody(stem: number): string {
  const s = hex(stem);
  return `
    <ellipse cx="22" cy="53" rx="16" ry="3" fill="#2a1a0a" opacity="0.3" />
    <path d="M20 52 L16 40 L21 24 L18 14" fill="none" stroke="${s}" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="miter" />
    <path d="M20 52 L27 33 L22 18 L27 7" fill="none" stroke="${s}" stroke-width="3" stroke-linecap="round" stroke-linejoin="miter" />
    <path d="M22 50 L32 38 L35 24 L31 15" fill="none" stroke="${s}" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="miter" />
    <path d="M22 51 L12 40 L9 26 L13 17" fill="none" stroke="${s}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="miter" />
    <path d="M18 14 l-3 -4 M18 14 l3 -3" stroke="#2a1a0a" stroke-width="2" stroke-linecap="round" />
    <path d="M27 7 l-2 -4 M27 7 l3 -2" stroke="#2a1a0a" stroke-width="2" stroke-linecap="round" />
    <path d="M31 15 l3 -3 M31 15 l1 -4" stroke="#2a1a0a" stroke-width="1.8" stroke-linecap="round" />
    <path d="M13 17 l-3 -2 M13 17 l-1 -4" stroke="#2a1a0a" stroke-width="1.8" stroke-linecap="round" />`;
}

/** Moor stone: weathered grey-green angular stone with lichen patches. */
export function moorStoneBody(stone: number): string {
  const s = hex(stone);
  const dark = '#3a4a3f';
  const lichen = '#6a7a62';
  return `
    <path d="M4 40 L10 16 L28 6 L50 12 L54 30 L44 40 L18 41 Z" fill="${s}" />
    <path d="M28 6 L50 12 L54 30 L38 24 Z" fill="${dark}" />
    <path d="M4 40 L10 16 L22 20 L18 41 Z" fill="${dark}" opacity="0.55" />
    <ellipse cx="20" cy="18" rx="6" ry="4" fill="${lichen}" opacity="0.5" transform="rotate(-18 20 18)" />
    <ellipse cx="40" cy="30" rx="5" ry="3.5" fill="${lichen}" opacity="0.5" transform="rotate(14 40 30)" />
    <ellipse cx="12" cy="34" rx="4" ry="2.6" fill="${lichen}" opacity="0.5" />`;
}

/** Mist shelf: low faceted grey-green stone ledge with crisp stylized ground mist wisps. */
export function mistShelfBody(ledge: number): string {
  const l = hex(ledge);
  const fog = '#aab3a6';
  const darkStone = '#3a4a3f';
  return `
    <!-- Faceted rock shelf body -->
    <path d="M2 24 L14 8 L86 6 L98 22 L92 30 L10 30 Z" fill="${l}" />
    <path d="M2 24 L14 8 L30 12 L18 28 Z" fill="${darkStone}" opacity="0.5" />
    <path d="M86 6 L98 22 L90 28 L78 14 Z" fill="${darkStone}" opacity="0.35" />
    <!-- Stylized pooled fog veil with crisp contour edges -->
    <path d="M6 24 Q32 17 62 20 Q84 18 94 24 Q82 28 50 28 Q18 27 6 24 Z" fill="${fog}" opacity="0.45" />
    <path d="M18 22 Q42 16 68 18 Q82 20 74 23 Q48 21 18 22 Z" fill="${fog}" opacity="0.38" />
    <path d="M28 20 Q50 14 72 17" stroke="${fog}" stroke-width="1.2" fill="none" opacity="0.5" stroke-linecap="round" />`;
}

/** Fallen log: horizontal dark log with broken branches and moss on shaded side. */
export function fallenLogBody(log: number): string {
  const l = hex(log);
  return `
    <ellipse cx="60" cy="33" rx="56" ry="3.5" fill="#2a1a0a" opacity="0.3" />
    <path d="M4 30 Q60 20 116 30 Q118 34 116 37 Q60 30 4 37 Q2 34 4 30 Z" fill="${l}" />
    <ellipse cx="6" cy="33" rx="4" ry="4.5" fill="#2a1a0a" />
    <ellipse cx="114" cy="33" rx="4.5" ry="5" fill="#2a1a0a" />
    <path d="M18 24 q-2 -6 -6 -8 M96 22 q3 -6 7 -7" fill="none" stroke="#2a1a0a" stroke-width="2.4" stroke-linecap="round" />
    <path d="M12 27 q6 -1 8 3 M40 22 q6 -1 8 3 M74 22 q-6 -1 -8 3" fill="none" stroke="#4a5a4a" stroke-width="4" opacity="0.6" stroke-linecap="round" />`;
}

/** Gate: open weathered timber boundary gateway with arched overhead lintel and open gate leaves. */
export function gateBody(wood: number): string {
  const w = hex(wood);
  const wDark = '#3a2a1a';
  const iron = '#2a2a2a';
  return `
    <!-- Boundary gateway posts -->
    <rect x="4" y="8" width="10" height="84" rx="2" fill="${wDark}" />
    <rect x="54" y="8" width="10" height="84" rx="2" fill="${wDark}" />
    <!-- Post stone footers and caps -->
    <path d="M2 90 L16 90 L14 92 L4 92 Z M52 90 L66 90 L64 92 L54 92 Z" fill="${iron}" opacity="0.8" />
    <path d="M3 8 L15 8 L13 4 L5 4 Z M53 8 L65 8 L63 4 L55 4 Z" fill="${wDark}" />
    <!-- Overhead lintel beam -->
    <path d="M2 12 Q34 6 66 12 L66 20 Q34 14 2 20 Z" fill="${w}" />
    <path d="M4 14 Q34 9 64 14" stroke="${wDark}" stroke-width="1.2" fill="none" opacity="0.6" />
    <rect x="30" y="2" width="8" height="6" rx="1.5" fill="${iron}" />
    <!-- Iron bracket reinforcement -->
    <path d="M4 20 L18 20 L18 24 L8 24 L8 34 L4 34 Z M64 20 L50 20 L50 24 L60 24 L60 34 L64 34 Z" fill="${iron}" />
    <!-- Open gate leaves (swung wide open into perspective, center passage clear) -->
    <path d="M6 24 L14 28 L14 84 L6 88 Z" fill="${w}" />
    <path d="M6 34 L14 38 M6 56 L14 60 M6 76 L14 80" stroke="${wDark}" stroke-width="2.5" />
    <path d="M62 24 L54 28 L54 84 L62 88 Z" fill="${w}" />
    <path d="M62 34 L54 38 M62 56 L54 60 M62 76 L54 80" stroke="${wDark}" stroke-width="2.5" />
    <!-- Iron strap hinges -->
    <rect x="2" y="32" width="6" height="4" rx="1" fill="${iron}" />
    <rect x="2" y="74" width="6" height="4" rx="1" fill="${iron}" />
    <rect x="60" y="32" width="6" height="4" rx="1" fill="${iron}" />
    <rect x="60" y="74" width="6" height="4" rx="1" fill="${iron}" />`;
}

// ─── Tier-3 generators ──────────────────────────────────────────────────────

/** Colonnade column: tall stone support with capital and glowing cracks. */
export function colonnadeColumnBody(stone: number): string {
  const s = hex(stone);
  const darkBase = '#0a1a08';
  const capital = '#2a3a28';
  const crack = '#6b8a62';
  return `
    <path d="M14 140 L10 30 Q9 18 16 10 L30 10 Q37 18 36 30 L32 140 Z" fill="${s}" />
    <path d="M14 140 L10 30 Q9 18 16 10 L23 10 L23 140 Z" fill="${darkBase}" opacity="0.55" />
    <path d="M8 10 L38 10 L34 22 L12 22 Z" fill="${capital}" />
    <path d="M6 6 L40 6 L38 10 L8 10 Z" fill="${capital}" opacity="0.85" />
    <path d="M18 34 L15 58 M15 58 L20 60 M20 60 L17 88" fill="none" stroke="${crack}" stroke-width="1.6" opacity="0.4" />
    <path d="M27 46 L30 70 M30 70 L26 74 M26 74 L29 100" fill="none" stroke="${crack}" stroke-width="1.6" opacity="0.4" />`;
}

/** Broken pillar: snapped column with fallen rubble chunks. */
export function brokenPillarBody(stone: number): string {
  const s = hex(stone);
  const darkBase = '#0a1a08';
  const broken = '#2a3a28';
  return `
    <rect x="6" y="28" width="16" height="28" fill="${s}" />
    <rect x="6" y="28" width="7" height="28" fill="${darkBase}" opacity="0.5" />
    <path d="M6 28 L22 28 L19 22 L9 22 Z" fill="${broken}" />
    <path d="M40 20 L98 8 L104 22 L46 36 Z" fill="${s}" />
    <path d="M40 20 L58 16 L60 30 L46 36 Z" fill="${darkBase}" opacity="0.45" />
    <ellipse cx="40" cy="20" rx="6" ry="7" fill="${broken}" />
    <path d="M28 52 l8 -6 l6 4 l-4 6 Z" fill="${s}" opacity="0.85" />
    <path d="M60 48 l10 -5 l7 5 l-6 5 Z" fill="${s}" opacity="0.7" />
    <path d="M84 50 l7 -4 l5 4 l-4 4 Z" fill="${darkBase}" opacity="0.8" />`;
}

/** Dais: raised circular stone platform with glowing cracks. */
export function daisBody(stone: number): string {
  const s = hex(stone);
  const edgeDark = '#1a2a18';
  const trim = '#3a4a38';
  const crack = '#6b8a62';
  return `
    <ellipse cx="70" cy="26" rx="68" ry="20" fill="${edgeDark}" />
    <ellipse cx="70" cy="24" rx="62" ry="17" fill="${s}" />
    <ellipse cx="70" cy="24" rx="62" ry="17" fill="none" stroke="${trim}" stroke-width="3" opacity="0.85" />
    <ellipse cx="70" cy="18" rx="40" ry="9" fill="${trim}" opacity="0.35" />
    <path d="M70 24 L70 8 M70 24 L48 14 M70 24 L92 14 M70 24 L40 26 M70 24 L100 26 M70 24 L55 34 M70 24 L85 34"
      stroke="${crack}" stroke-width="1.4" opacity="0.3" fill="none" />`;
}

/** Spiral ledge: curved stone staircase ledge with faint glowing fissures. */
export function spiralLedgeBody(stone: number): string {
  const s = hex(stone);
  const darkBase = '#0a1a08';
  const crack = '#6b8a62';
  return `
    <path d="M2 26 Q30 4 60 10 Q86 15 98 28 L92 32 Q80 20 58 16 Q32 11 8 30 Z" fill="${s}" />
    <path d="M2 26 Q30 4 60 10 L58 16 Q32 11 8 30 Z" fill="${darkBase}" opacity="0.5" />
    <path d="M20 20 L26 24 L22 28 M50 12 L55 17 L50 20 M76 18 L82 22 L78 26"
      stroke="${crack}" stroke-width="1.3" opacity="0.3" fill="none" />`;
}

/** Bioluminescent fungus: glowing mushrooms with radial glow halo. */
export function bioluminescentFungusBody(cap: number): string {
  const c = hex(cap);
  const stem = '#4a5a4a';
  return `
    <defs>
      <radialGradient id="bfg" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0" stop-color="${c}" stop-opacity="0.9" />
        <stop offset="0.5" stop-color="${c}" stop-opacity="0.4" />
        <stop offset="1" stop-color="${c}" stop-opacity="0" />
      </radialGradient>
    </defs>
    <circle cx="13" cy="12" r="13" fill="url(#bfg)" />
    <path d="M5 18 v-6 M11 19 v-8 M15 19 v-7 M20 18 v-6"
      stroke="${stem}" stroke-width="1.4" stroke-linecap="round" />
    <ellipse cx="5" cy="11" rx="3" ry="2.2" fill="${c}" opacity="0.6" />
    <ellipse cx="11" cy="9.5" rx="3.6" ry="2.6" fill="${c}" opacity="0.6" />
    <ellipse cx="15" cy="10.5" rx="3.2" ry="2.4" fill="${c}" opacity="0.6" />
    <ellipse cx="20" cy="11" rx="2.6" ry="2" fill="${c}" opacity="0.6" />`;
}

// ─── Lanternwood landmark ───────────────────────────────────────────────────

/** Lanternwood landmark: broad, asymmetric dead tree with crisp faceted silhouette and readable hollow. */
export function gnarledTreeBody(wood: number): string {
  const w = hex(wood);
  const mid = hex(darken(wood, 0.78));
  const dark = hex(darken(wood, 0.42));
  return `
    <!-- Ground contact shadow -->
    <ellipse cx="61" cy="156" rx="44" ry="5" fill="${dark}" opacity="0.45" />
    <!-- Main trunk silhouette with natural bark taper -->
    <path d="M44 158
      Q46 130 40 105
      Q35 82 47 60
      L39 43 L49 49
      Q54 28 48 11
      L59 27
      Q66 15 70 2
      Q72 22 66 40
      Q78 31 94 24
      L80 42
      Q99 39 113 48
      Q92 48 76 56
      Q68 64 70 79
      Q79 70 91 66
      L82 79
      Q96 79 106 88
      Q88 83 73 91
      Q67 116 78 158 Z"
      fill="${w}" />
    <!-- Secondary midtone trunk layer -->
    <path d="M44 158 Q48 124 43 102 Q40 82 51 61 Q56 48 59 27
      Q65 45 62 64 Q56 84 61 105 Q67 131 63 158 Z"
      fill="${mid}" opacity="0.78" />
    <!-- Deep hollow socket -->
    <path d="M54 151 Q50 133 54 118 Q59 105 66 116 Q71 132 66 151 Z"
      fill="${dark}" />
    <path d="M56 147 Q55 132 59 123 Q63 118 65 128 Q67 138 64 147 Z"
      fill="#080e0f" />
    <!-- Crisp faceted branch accents without blurry bloated strokes -->
    <path d="M48 76 Q31 64 7 67 Q27 58 45 63 M72 57 Q91 45 114 49
      M69 90 Q86 81 105 88"
      fill="none" stroke="${mid}" stroke-width="4.5" stroke-linecap="round"
      stroke-linejoin="round" />
    <!-- Root flares -->
    <path d="M42 156 L25 160 M52 156 L40 160 M69 156 L88 160"
      fill="none" stroke="${dark}" stroke-width="4" stroke-linecap="round" />
    <!-- Fissure bark detail -->
    <path d="M51 91 Q57 84 63 91 M48 109 Q55 103 62 109"
      fill="none" stroke="${dark}" stroke-width="1.8" opacity="0.7" />`;
}
